import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Play, LoaderCircle, Video } from "lucide-react";

import { formatRelativeTime } from "../../../utils/datetime/formatRelativeTime";
import { formatVideoDuration } from "../../../utils/helpers/format/formatVideoDuration";
import { SaveBadge } from "../../badges/SaveBadge";
import { EllipsisMenu } from "../../menu/ellipsis/EllipsisMenu";

import "./VideoThumbnailCard.css";

const VideoThumbnailCard = ({
  video,
  onClick,
  disabled = false,
  showPlayButton = true,
  showMenuButton = false,
  menuOptions = [],
  handleToggleSave,
  hasSubscribed = true,
  className = "",
}) => {
  const navigate = useNavigate();

  const title = video?.title || "Untitled Video";
  const username = video?.username;
  const createdAt = video?.createdAt;
  const duration = video?.duration ?? video?.videoDuration;

  const accessStatus = String(video?.accessStatus ?? "")
    .trim()
    .toLowerCase();

  const hlsStatus = String(video?.hlsStatus ?? "")
    .trim()
    .toLowerCase();

  const isProcessing = ["processing", "pending"].includes(hlsStatus);

  const isRental = ["rent_to_watch", "rent_watch"].includes(accessStatus);

  const canPlay =
    ["purchased", "free_watch"].includes(accessStatus) ||
    video?.canWatch === true;

  const isLocked = !isProcessing && !canPlay && (isRental || !hasSubscribed);

  const showStatusIcon =
    showPlayButton && (isProcessing || isLocked || canPlay);

  const showMenu = showMenuButton && menuOptions.length > 0;

  const handleCardClick = () => {
    if (disabled) return;

    onClick?.(video);
  };

  const handleCardKeyDown = (event) => {
    if (disabled || !onClick) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardClick();
    }
  };

  const handleCreatorClick = (event) => {
    event.stopPropagation();

    if (disabled || !username) return;

    navigate(`/${username}`);
  };

  const handleSaveClick = (event) => {
    event?.stopPropagation?.();

    if (disabled) return;

    handleToggleSave?.(video);
  };

  const safeMenuOptions = menuOptions.map((option) => ({
    ...option,
    action: (event) => {
      event?.stopPropagation?.();

      if (disabled) return;

      option.action?.(event, video);
    },
  }));

  const cardClassName = [
    "video-thumb-card",
    onClick && !disabled && "video-thumb-card--clickable",
    disabled && "video-thumb-card--disabled",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      className={cardClassName}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-disabled={onClick ? disabled : undefined}
    >
      <div className="video-thumb-card__thumbnail">
        {video?.thumbnailUrl ? (
          <img
            src={video.thumbnailUrl}
            alt={`${title} thumbnail`}
            className="video-thumb-card__img"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="video-thumb-card__placeholder">
            <Video size={24} aria-hidden="true" />
          </div>
        )}

        {showStatusIcon && (
          <div className="video-thumb-card__status-overlay">
            <div
              className="video-thumb-card__status-icon"
              aria-label={
                isProcessing
                  ? "Video processing"
                  : isLocked
                    ? "Rental required"
                    : "Video available to watch"
              }
            >
              {isProcessing ? (
                <LoaderCircle
                  size={24}
                  className="video-thumb-card__spinner"
                  aria-hidden="true"
                />
              ) : isLocked ? (
                <Lock size={24} aria-hidden="true" />
              ) : (
                <Play size={24} fill="currentColor" aria-hidden="true" />
              )}
            </div>
          </div>
        )}

        {handleToggleSave && (
          <div
            className="video-thumb-card__save"
            onClick={(event) => event.stopPropagation()}
          >
            <SaveBadge hasSaved={video?.hasSaved} onToggle={handleSaveClick} />
          </div>
        )}

        {duration != null && Number(duration) > 0 && (
          <div className="video-thumb-card__duration">
            {formatVideoDuration(duration)}
          </div>
        )}
      </div>

      <div className="video-thumb-card__body">
        <div className="video-thumb-card__details">
          <h3 className="video-thumb-card__title">{title}</h3>

          {(username || createdAt) && (
            <div className="video-thumb-card__meta">
              {username && (
                <span
                  className="video-thumb-card__username"
                  onClick={handleCreatorClick}
                >
                  {username}
                </span>
              )}

              {username && createdAt && (
                <span className="video-thumb-card__divider">•</span>
              )}

              {createdAt && (
                <span className="video-thumb-card__created-at">
                  {formatRelativeTime(createdAt)}
                </span>
              )}
            </div>
          )}
        </div>

        {showMenu && (
          <div
            className="video-thumb-card__menu"
            onClick={(event) => event.stopPropagation()}
          >
            <EllipsisMenu
              options={safeMenuOptions}
              position="right"
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </article>
  );
};

export default VideoThumbnailCard;
